/**
 * 회원 관리 비즈니스 로직
 * 필터링, 검색, UI 헬퍼 함수
 */

import type { Member, MembershipType, MemberStatus } from '../models';

export class MemberService {
  /**
   * 회원 목록 필터링
   */
  filterMembers(
    members: Member[],
    searchTerm: string,
    filterStatus: 'all' | MemberStatus,
    filterMembership: 'all' | MembershipType
  ): Member[] {
    const searchLower = searchTerm.toLowerCase();

    return members.filter((member) => {
      // 검색어 매칭
      const matchesSearch =
        member.name.toLowerCase().includes(searchLower) ||
        member.phone.includes(searchTerm) ||
        (member.member_id && member.member_id.toLowerCase().includes(searchLower)) ||
        (member.memberNumber && member.memberNumber.toLowerCase().includes(searchLower));

      // 상태 필터링
      const matchesStatus = filterStatus === 'all' || member.status === filterStatus;

      // 이용권 필터링
      const matchesMembership =
        filterMembership === 'all' || member.membershipType === filterMembership;

      return matchesSearch && matchesStatus && matchesMembership;
    });
  }

  /**
   * 회원 통계 계산
   */
  calculateMemberStats(members: Member[]): {
    total: number;
    active: number;
    inactive: number;
    suspended: number;
  } {
    return {
      total: members.length,
      active: members.filter((m) => m.status === 'active').length,
      inactive: members.filter((m) => m.status === 'inactive').length,
      suspended: members.filter((m) => m.status === 'suspended').length,
    };
  }

  /**
   * 회원 입력 데이터 검증
   */
  validateMemberData(data: { name: string; phone: string; email?: string }): {
    valid: boolean;
    message?: string;
  } {
    if (!data.name || !data.phone) {
      return {
        valid: false,
        message: '이름과 전화번호는 필수 입력 사항입니다.',
      };
    }

    // 전화번호 형식 간단 검증
    const phoneRegex = /^[0-9-]+$/;
    if (!phoneRegex.test(data.phone)) {
      return {
        valid: false,
        message: '전화번호 형식이 올바르지 않습니다.',
      };
    }

    // 이메일 형식 간단 검증 (제공된 경우)
    if (data.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        return {
          valid: false,
          message: '이메일 형식이 올바르지 않습니다.',
        };
      }
    }

    return { valid: true };
  }

  /**
   * 회원번호 생성 (임시 이메일용)
   */
  generateTemporaryEmail(): string {
    return `member_${Date.now()}@studyroom.local`;
  }

  /**
   * 회원 얼굴 인식 ID 가져오기
   */
  getFaceUserId(member: Member): string {
    return member.member_id || member.memberNumber || member.id;
  }

  /**
   * UTC 시간을 한국 시간(KST)으로 변환
   */
  formatToKoreanTime(utcTime: string | null | undefined): string {
    if (!utcTime) return '-';

    try {
      // Supabase에서 받은 timestamp는 항상 UTC
      // 형식: "2025-10-12T04:01:21+00:00" 또는 "2025-10-12 04:01:21"

      // timestamp 끝에 Z가 없으면 추가 (UTC로 명시)
      let utcTimestamp = utcTime;
      if (!utcTime.endsWith('Z') && !utcTime.includes('+')) {
        // "2025-10-12 04:01:21" → "2025-10-12T04:01:21Z"
        utcTimestamp = utcTime.replace(' ', 'T') + 'Z';
      }

      // UTC 시간 파싱
      const date = new Date(utcTimestamp);

      // KST 옵션으로 포맷팅 (자동으로 +9시간)
      const kstString = date.toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // "2025. 10. 12. 13:01" → "2025-10-12 13:01"
      return kstString
        .replace(/\. /g, '-')
        .replace('. ', ' ')
        .replace('.', '');
    } catch (error) {
      console.error('시간 변환 오류:', error);
      return utcTime;
    }
  }
}

// 싱글톤 인스턴스
export const memberService = new MemberService();
